import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import './JobApplication.css';

const JobApplication = ({ jobTitle = '', location = 'CYNTRA' }) => {
  const location_data = useLocation();
  const position = location_data.state?.position;
  // const fileInputRef = useRef(null);
  const formRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedinProfile: '',
    resume: null,
    introduction: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Map form field names to state field names
    const fieldMap = {
      'from_name': 'name',
      'from_email': 'email',
      'phone': 'phone',
      'linkedin_profile': 'linkedinProfile',
      'introduction': 'introduction'
    };
    
    const stateFieldName = fieldMap[name] || name;
    
    setFormData(prevState => ({
      ...prevState,
      [stateFieldName]: value
    }));
  };

  // const handleFileChange = (e) => {
  //   setFormData(prevState => ({
  //     ...prevState,
  //     resume: e.target.files[0]
  //   }));
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // For testing purposes, let's log the form data first
      console.log('Form data being submitted:', formData);
      console.log('Position data:', position);

      // Debug: Check form data before sending
      const formData_debug = new FormData(formRef.current);
      console.log('Form entries:');
      for (let [key, value] of formData_debug.entries()) {
        console.log(key, ':', value);
      }

      // EmailJS Public Key
      const publicKey = 'Tv1gtqU6Y9UOKRXAR';
      
      console.log('Sending email with EmailJS sendForm...');
      console.log('Service ID:', 'service_twv4tf9');
      console.log('Template ID:', 'template_8wir2sa');
      console.log('Public Key:', publicKey);

      // Send form using EmailJS sendForm method (supports file attachments)
      const response = await emailjs.sendForm(
        'service_twv4tf9',
        'template_8wir2sa',
        formRef.current,
        publicKey
      );

      console.log('EmailJS response:', response);

      if (response.status === 200) {
        setSubmitStatus('success');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          linkedinProfile: '',
          resume: null,
          introduction: ''
        });
        // Reset the actual form as well
        formRef.current.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Email sending failed:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        text: error.text,
        name: error.name
      });
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="job-application-container">
      <div className="application-header">
        <div className="breadcrumb">
          <span>Jobs</span> / <span>{position?.title || jobTitle}</span>
        </div>
        <h1>Job Application Form</h1>
      </div>

      <div className="application-content">
        <div className="application-form">
          <form ref={formRef} onSubmit={handleSubmit}>
            {/* Hidden fields for EmailJS template variables */}
            <input type="hidden" name="to_name" value="CyntraLabs HR Team" />
            <input type="hidden" name="job_title" value={position?.title || jobTitle} />
            <input type="hidden" name="department" value={position?.department || 'Engineering'} />
            <input type="hidden" name="location" value={position?.location || location} />

            <div className="form-group">
              <label htmlFor="from_name">Your Name *</label>
              <input
                type="text"
                id="from_name"
                name="from_name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="from_email">Your Email *</label>
              <input
                type="email"
                id="from_email"
                name="from_email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Your Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="linkedin_profile">LinkedIn Profile</label>
              <div className="linkedin-input">
                <span className="linkedin-icon">in</span>
                <input
                  type="url"
                  id="linkedin_profile"
                  name="linkedin_profile"
                  value={formData.linkedinProfile}
                  onChange={handleInputChange}
                  placeholder="e.g. https://www.linkedin.com/in/fpodoo/"
                />
              </div>
            </div>

            {/* <div className="form-group">
              <label htmlFor="attachment">Resume</label>
              <input
                ref={fileInputRef}
                type="file"
                id="attachment"
                name="attachment"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
                className="file-input-field"
              />
              <p className="file-note">The resume is optional if you have a LinkedIn profile</p>
            </div> */}

            <div className="form-group">
              <label htmlFor="introduction">Short Introduction</label>
              <textarea
                id="introduction"
                name="introduction"
                value={formData.introduction}
                onChange={handleInputChange}
                placeholder="Optional introduction, or any question you might have about the job..."
                rows={6}
              />
            </div>

            <button 
              type="submit" 
              className="submit-btn" 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>

            {submitStatus === 'success' && (
              <div className="success-message">
                <p>Thank you! Your application has been submitted successfully. We'll get back to you soon.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="error-message">
                <p>Sorry, there was an error submitting your application. Please try again or contact us directly.</p>
              </div>
            )}
          </form>
        </div>

        <div className="job-details">
          <h2>Job Description</h2>
          <Link to="/careers" className="back-link">← Back to Careers</Link>
          
          <div className="details-section">
            <h3>Job Title</h3>
            <p>{position?.title || jobTitle}</p>
          </div>

          {position && (
            <>
              <div className="details-section">
                <h3>Department</h3>
                <p>{position.department}</p>
              </div>

              <div className="details-section">
                <h3>Location</h3>
                <p>{position.location}</p>
              </div>

              <div className="details-section">
                <h3>Job Description</h3>
                <p>{position.description}</p>
              </div>

              <div className="details-section">
                <h3>Key Responsibilities</h3>
                <ul>
                  {position.responsibilities?.map((responsibility, index) => (
                    <li key={index}>{responsibility}</li>
                  ))}
                </ul>
              </div>

              <div className="details-section">
                <h3>Required Education</h3>
                <p>{position.requirements?.education}</p>
              </div>

              <div className="details-section">
                <h3>Experience Required</h3>
                <p>{position.requirements?.experience}</p>
              </div>

              <div className="details-section">
                <h3>Technical Skills</h3>
                <div className="skills-grid">
                  <div>
                    <h4>Integration Platforms</h4>
                    <ul>
                      {position.requirements?.technicalSkills?.integrationPlatforms?.map((skill, index) => (
                        <li key={index}>{skill}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4>ERP Systems</h4>
                    <ul>
                      {position.requirements?.technicalSkills?.erpSystems?.map((skill, index) => (
                        <li key={index}>{skill}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4>Languages & Protocols</h4>
                    <ul>
                      {position.requirements?.technicalSkills?.languages?.map((skill, index) => (
                        <li key={index}>{skill}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="details-section">
                <h3>Travel Requirements</h3>
                <p>{position.requirements?.travel}</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobApplication; 